import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../api/axiosConfig';
import './ManagementPages.css';
import './ValidationPage.css';
import './VisitDetailPage.css';

// Consultation des contrôles terrain, partagée par l'administrateur, le responsable et le
// chef de zone. Le serveur restreint déjà l'accès : ni marchandiseur, ni superviseur en chef.
// La présentation reprend les classes partagées (data-table, status-badge, action-button)
// pour que la page soit identique aux autres écrans de suivi.

// Les appréciations réutilisent les variantes de pastille déjà définies dans l'application
const APPRECIATIONS = {
  CONFORME: { libelle: 'Conforme', classe: 'valide' },
  A_AMELIORER: { libelle: 'À améliorer', classe: 'pending' },
  NON_CONFORME: { libelle: 'Non conforme', classe: 'rejete' },
};
const PRESENCES = { PRESENT: 'Présent', ABSENT: 'Pas présent', DEJA_PASSE: 'Déjà passé' };
const COHERENCES = { OUI: 'Oui', NON: 'Non', ECART_CONSTATE: 'Écart constaté' };

// Un contrôle porte soit sur le merchandiser du point, soit sur l'hôtesse du supermarché
const personneControlee = (c) => (c.hotesse_nom ? `${c.hotesse_nom} (hôtesse)` : c.merchandiser_nom);

const ouiNon = (v) => (v === true ? 'Oui' : v === false ? 'Non' : 'N/A');

const dureeControle = (c) => {
  if (!c?.heure_debut || !c?.heure_fin) return 'N/A';
  const [h1, m1] = c.heure_debut.split(':').map(Number);
  const [h2, m2] = c.heure_fin.split(':').map(Number);
  const minutes = (h2 * 60 + m2) - (h1 * 60 + m1);
  return minutes >= 0 ? `${minutes} min` : 'N/A';
};

function ControlesTerrainPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [controles, setControles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [filtres, setFiltres] = useState({ base: '', zone: '', date_debut: '', date_fin: '' });

  const charger = useCallback(async () => {
    setIsLoading(true);
    setErreur(null);
    try {
      const params = new URLSearchParams();
      Object.entries(filtres).forEach(([cle, valeur]) => { if (valeur) params.append(cle, valeur); });
      const reponse = await axiosInstance.get(`/controles-terrain/?${params.toString()}`);
      setControles(Array.isArray(reponse.data) ? reponse.data : []);
    } catch (e) {
      setErreur(e.response?.status === 403
        ? "Vous n'avez pas accès aux contrôles terrain."
        : "Impossible de charger les contrôles. Réessayez dans un instant.");
    } finally {
      setIsLoading(false);
    }
  }, [filtres]);

  useEffect(() => { charger(); }, [charger]);


  const exporter = () => {
    const entetes = ['Date', 'Heure début', 'Heure fin', 'Durée', 'Base', 'Zone', 'Client',
      'Contrôleur', 'Personne contrôlée', 'Sur site', 'Distance (m)', 'Présence', 'Avis global'];
    const lignes = controles.map(c => [
      c.date_controle, c.heure_debut || '', c.heure_fin || '', dureeControle(c),
      c.base || '', c.zone || '', c.client_nom || '', c.controleur_nom || '',
      personneControlee(c) || '', c.est_sur_site ? 'Oui' : 'Non',
      c.distance_client != null ? Math.round(c.distance_client) : '',
      PRESENCES[c.presence_marchandiser] || '',
      APPRECIATIONS[c.appreciation]?.libelle || '',
    ]);
    const csv = [entetes, ...lignes]
      .map(l => l.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
    // BOM pour que les accents s'affichent correctement dans Excel
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const lien = document.createElement('a');
    lien.href = URL.createObjectURL(blob);
    lien.download = `controles_terrain_${new Date().toISOString().slice(0, 10)}.csv`;
    lien.click();
  };

  const champ = { padding: '7px 10px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px' };
  const libelle = { fontSize: '12px', fontWeight: '600', color: '#495057' };
  const colonne = { display: 'flex', flexDirection: 'column', gap: '4px' };

  return (
    <div className="management-page" style={{ padding: '20px' }}>
      <h1 style={{ color: '#2c3e50', margin: 0 }}>Contrôles Terrain</h1>
      <p style={{ color: '#6c757d', marginTop: '6px' }}>
        Passages des contrôleurs dans les points de vente : où, quoi, et combien de temps.
      </p>

      <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
          <div style={colonne}>
            <label style={libelle}>Base</label>
            <input style={{ ...champ, minWidth: '140px' }} placeholder="DOUALA, YAOUNDE..."
              value={filtres.base} onChange={(e) => setFiltres({ ...filtres, base: e.target.value })} />
          </div>
          <div style={colonne}>
            <label style={libelle}>Zone</label>
            <input style={{ ...champ, minWidth: '140px' }} placeholder="DOUALA 3..."
              value={filtres.zone} onChange={(e) => setFiltres({ ...filtres, zone: e.target.value })} />
          </div>
          <div style={colonne}>
            <label style={libelle}>Date début</label>
            <input type="date" style={champ} value={filtres.date_debut}
              onChange={(e) => setFiltres({ ...filtres, date_debut: e.target.value })} />
          </div>
          <div style={colonne}>
            <label style={libelle}>Date fin</label>
            <input type="date" style={champ} value={filtres.date_fin}
              onChange={(e) => setFiltres({ ...filtres, date_fin: e.target.value })} />
          </div>
          <button
            onClick={() => setFiltres({ base: '', zone: '', date_debut: '', date_fin: '' })}
            style={{ padding: '7px 18px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
            Réinitialiser
          </button>
          <button
            onClick={exporter} disabled={controles.length === 0}
            style={{ padding: '7px 18px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: '4px', cursor: controles.length ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: '600', opacity: controles.length ? 1 : 0.5 }}>
            Exporter en Excel
          </button>
        </div>
        <div style={{ marginTop: '8px', fontSize: '13px', color: '#6c757d' }}>
          {controles.length} contrôle{controles.length > 1 ? 's' : ''} affiché{controles.length > 1 ? 's' : ''}
        </div>
      </div>

      {erreur && <p className="no-data-message" style={{ color: '#dc3545' }}>{erreur}</p>}

      <div className="management-table-container">
        {isLoading ? (
          <p style={{ textAlign: 'center', padding: '40px' }}>Chargement des contrôles...</p>
        ) : controles.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Client</th>
                <th>Zone</th>
                <th>Contrôleur</th>
                <th>Personne contrôlée</th>
                <th>Horaires</th>
                <th>Durée</th>
                <th>Sur site</th>
                <th>Présence</th>
                <th>Avis global</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {controles.map(c => {
                const a = APPRECIATIONS[c.appreciation];
                return (
                  <tr key={c.id}>
                    <td>{new Date(c.date_controle).toLocaleDateString()}</td>
                    <td><strong>{c.client_nom || 'N/A'}</strong></td>
                    <td>{c.zone || 'N/A'}</td>
                    <td>{c.controleur_nom || 'N/A'}</td>
                    <td>{personneControlee(c) || 'N/A'}</td>
                    <td>{c.heure_debut || 'N/A'} → {c.heure_fin || 'N/A'}</td>
                    <td>{dureeControle(c)}</td>
                    <td>
                      {c.est_sur_site
                        ? <span className="status-badge valide">
                            {c.distance_client != null ? `${Math.round(c.distance_client)} m` : 'Oui'}
                          </span>
                        : <span className="status-badge rejete">Non</span>}
                    </td>
                    <td>{PRESENCES[c.presence_marchandiser] || 'N/A'}</td>
                    <td>{a ? <span className={`status-badge ${a.classe}`}>{a.libelle}</span> : 'N/A'}</td>
                    <td>
                      <button
                        onClick={() => navigate(`${location.pathname.replace(/\/controles-terrain.*$/, '')}/controle-terrain/${c.id}`)}
                        className="action-button view-button">
                        Voir Détails
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="no-data-message">Aucun contrôle trouvé pour ces critères.</p>
        )}
      </div>

    </div>
  );
}




export default ControlesTerrainPage;
